// Comprehensive integration icon mapping for n8n nodes
// Maps n8n node types to Simple Icons CDN slugs
// CDN URL: https://cdn.simpleicons.org/{slug}/{color}

export interface IntegrationIconInfo {
  name: string;
  slug: string; // Simple Icons slug, empty string = use letter fallback
  color: string; // Hex color (with #)
  bgColor: string; // Light background color
}

// Base service mapping (service key -> icon info)
// Node variants like *Tool, *Trigger share the same icon
const BASE_ICONS: Record<string, IntegrationIconInfo> = {
  // === Communication & Messaging ===
  slack: { name: 'Slack', slug: 'slack', color: '#4A154B', bgColor: '#F4EAF8' },
  discord: { name: 'Discord', slug: 'discord', color: '#5865F2', bgColor: '#EDEDFF' },
  telegram: { name: 'Telegram', slug: 'telegram', color: '#26A5E4', bgColor: '#E6F4FC' },
  microsoftTeams: { name: 'Teams', slug: 'microsoftteams', color: '#6264A7', bgColor: '#EEEEF6' },
  mattermost: { name: 'Mattermost', slug: 'mattermost', color: '#0058CC', bgColor: '#E6EFFA' },
  rocketchat: { name: 'Rocket.Chat', slug: 'rocketdotchat', color: '#F5455C', bgColor: '#FDE9EB' },
  zulip: { name: 'Zulip', slug: 'zulip', color: '#6492FE', bgColor: '#EEF2FF' },
  twist: { name: 'Twist', slug: '', color: '#44B2F4', bgColor: '#E9F6FE' },
  flock: { name: 'Flock', slug: '', color: '#4E2A8E', bgColor: '#EDE7F6' },
  whatsApp: { name: 'WhatsApp', slug: 'whatsapp', color: '#25D366', bgColor: '#E8F9EE' },
  line: { name: 'LINE', slug: 'line', color: '#00C300', bgColor: '#E6F9E6' },
  matrix: { name: 'Matrix', slug: 'matrix', color: '#000000', bgColor: '#F0F0F0' },
  mastodon: { name: 'Mastodon', slug: 'mastodon', color: '#6364FF', bgColor: '#EEEFFF' },
  bluesky: { name: 'Bluesky', slug: 'bluesky', color: '#0085FF', bgColor: '#E6F3FF' },

  // === Email ===
  gmail: { name: 'Gmail', slug: 'gmail', color: '#EA4335', bgColor: '#FDE7E7' },
  microsoftOutlook: { name: 'Outlook', slug: 'microsoftoutlook', color: '#0078D4', bgColor: '#E3F2FD' },
  sendGrid: { name: 'SendGrid', slug: 'sendgrid', color: '#1A82E2', bgColor: '#E3F2FD' },
  mailchimp: { name: 'Mailchimp', slug: 'mailchimp', color: '#FFE01B', bgColor: '#FFFDE6' },
  mailgun: { name: 'Mailgun', slug: 'mailgun', color: '#F06B66', bgColor: '#FDEEED' },
  mailjet: { name: 'Mailjet', slug: 'mailjet', color: '#0E8BFF', bgColor: '#E7F4FF' },
  postmark: { name: 'Postmark', slug: 'postmark', color: '#FFDE00', bgColor: '#FFFDE6' },
  sendinblue: { name: 'Brevo', slug: 'brevo', color: '#0B996E', bgColor: '#E7F5F0' },
  brevo: { name: 'Brevo', slug: 'brevo', color: '#0B996E', bgColor: '#E7F5F0' },
  mailerlite: { name: 'MailerLite', slug: 'mailerlite', color: '#09C269', bgColor: '#E7F8EF' },
  customerIo: { name: 'Customer.io', slug: 'customerdotio', color: '#27AE60', bgColor: '#E8F5EC' },
  convertKit: { name: 'ConvertKit', slug: 'convertkit', color: '#FB6970', bgColor: '#FEECED' },
  emailReadImap: { name: 'IMAP', slug: '', color: '#4285F4', bgColor: '#E8F0FE' },
  emailSend: { name: 'Email', slug: '', color: '#EA4335', bgColor: '#FDE7E7' },
  imap: { name: 'IMAP', slug: '', color: '#4285F4', bgColor: '#E8F0FE' },
  resend: { name: 'Resend', slug: 'resend', color: '#000000', bgColor: '#F0F0F0' },

  // === Google Suite ===
  googleSheets: { name: 'Sheets', slug: 'googlesheets', color: '#34A853', bgColor: '#E6F5EE' },
  googleDrive: { name: 'Drive', slug: 'googledrive', color: '#4285F4', bgColor: '#E8F0FE' },
  googleCalendar: { name: 'Calendar', slug: 'googlecalendar', color: '#4285F4', bgColor: '#E8F0FE' },
  googleDocs: { name: 'Docs', slug: 'googledocs', color: '#4285F4', bgColor: '#E8F0FE' },
  googleSlides: { name: 'Slides', slug: 'googleslides', color: '#FBBC04', bgColor: '#FEF7E0' },
  googleBigQuery: { name: 'BigQuery', slug: 'googlebigquery', color: '#669DF6', bgColor: '#EEF4FE' },
  googleCloudStorage: { name: 'Cloud Storage', slug: 'googlecloud', color: '#4285F4', bgColor: '#E8F0FE' },
  googleFirebaseCloudFirestore: { name: 'Firestore', slug: 'firebase', color: '#FFCA28', bgColor: '#FFF8E1' },
  googleFirebaseRealtimeDatabase: { name: 'Firebase', slug: 'firebase', color: '#FFCA28', bgColor: '#FFF8E1' },
  googleAnalytics: { name: 'Analytics', slug: 'googleanalytics', color: '#E37400', bgColor: '#FDF0E0' },
  googleAds: { name: 'Google Ads', slug: 'googleads', color: '#4285F4', bgColor: '#E8F0FE' },
  googleChat: { name: 'Google Chat', slug: 'googlechat', color: '#00AC47', bgColor: '#E6F6EB' },
  googleContacts: { name: 'Contacts', slug: 'googlecontacts', color: '#4285F4', bgColor: '#E8F0FE' },
  googleTasks: { name: 'Tasks', slug: 'googletasks', color: '#4285F4', bgColor: '#E8F0FE' },
  googlePerspective: { name: 'Perspective', slug: 'google', color: '#4285F4', bgColor: '#E8F0FE' },
  googleGemini: { name: 'Gemini', slug: 'googlegemini', color: '#8E75B2', bgColor: '#F3EFF7' },
  googleVertex: { name: 'Vertex AI', slug: 'googlecloud', color: '#4285F4', bgColor: '#E8F0FE' },
  googleTranslate: { name: 'Translate', slug: 'googletranslate', color: '#4285F4', bgColor: '#E8F0FE' },
  googleForms: { name: 'Google Forms', slug: 'googleforms', color: '#7A1FA2', bgColor: '#F3E9F7' },
  youTube: { name: 'YouTube', slug: 'youtube', color: '#FF0000', bgColor: '#FFE6E6' },
  gSuiteAdmin: { name: 'Google Admin', slug: 'google', color: '#4285F4', bgColor: '#E8F0FE' },

  // === Microsoft ===
  microsoftExcel: { name: 'Excel', slug: 'microsoftexcel', color: '#217346', bgColor: '#E8F1EC' },
  microsoftOneDrive: { name: 'OneDrive', slug: 'microsoftonedrive', color: '#0078D4', bgColor: '#E3F2FD' },
  microsoftSharepoint: { name: 'SharePoint', slug: 'microsoftsharepoint', color: '#0078D4', bgColor: '#E3F2FD' },
  microsoftToDo: { name: 'To Do', slug: 'microsofttodo', color: '#0078D4', bgColor: '#E3F2FD' },
  microsoftDynamicsCrm: { name: 'Dynamics', slug: 'dynamics365', color: '#002050', bgColor: '#E6E9EE' },
  microsoftEntraId: { name: 'Entra ID', slug: 'microsoftazure', color: '#0078D4', bgColor: '#E3F2FD' },
  microsoftSql: { name: 'SQL Server', slug: 'microsoftsqlserver', color: '#CC2927', bgColor: '#FAE8E8' },
  microsoft365: { name: 'Microsoft 365', slug: 'microsoft365', color: '#D83B01', bgColor: '#FCE8E2' },
  onenote: { name: 'OneNote', slug: 'microsoftonenote', color: '#7719AA', bgColor: '#F2E7F8' },

  // === Project Management ===
  notion: { name: 'Notion', slug: 'notion', color: '#000000', bgColor: '#F0F0F0' },
  asana: { name: 'Asana', slug: 'asana', color: '#F06A6A', bgColor: '#FDEAEA' },
  trello: { name: 'Trello', slug: 'trello', color: '#0052CC', bgColor: '#E3F2FD' },
  jira: { name: 'Jira', slug: 'jira', color: '#0052CC', bgColor: '#E3ECFB' },
  jiraSoftware: { name: 'Jira', slug: 'jirasoftware', color: '#0052CC', bgColor: '#E3ECFB' },
  monday: { name: 'Monday', slug: 'monday', color: '#FF3D57', bgColor: '#FFE8EB' },
  clickUp: { name: 'ClickUp', slug: 'clickup', color: '#7B68EE', bgColor: '#F0EEFF' },
  linear: { name: 'Linear', slug: 'linear', color: '#5E6AD2', bgColor: '#EEEFFA' },
  todoist: { name: 'Todoist', slug: 'todoist', color: '#E44332', bgColor: '#FCE9E7' },
  baserow: { name: 'Baserow', slug: 'baserow', color: '#5190EF', bgColor: '#ECF3FE' },
  wrike: { name: 'Wrike', slug: 'wrike', color: '#08CF65', bgColor: '#E7F9EE' },
  smartsheet: { name: 'Smartsheet', slug: 'smartsheet', color: '#0D122A', bgColor: '#E6E7E9' },
  teamwork: { name: 'Teamwork', slug: 'teamwork', color: '#6B47DC', bgColor: '#EFEBFB' },
  meistertask: { name: 'MeisterTask', slug: 'meistertask', color: '#00C4FF', bgColor: '#E6F9FF' },
  openProject: { name: 'OpenProject', slug: 'openproject', color: '#0770B8', bgColor: '#E6F1F8' },
  taiga: { name: 'Taiga', slug: 'taiga', color: '#70C542', bgColor: '#EFF9E9' },
  wekan: { name: 'Wekan', slug: '', color: '#006C9C', bgColor: '#E6F0F5' },
  vikunja: { name: 'Vikunja', slug: 'vikunja', color: '#FF7F3F', bgColor: '#FFF0E9' },

  // === CRM & Sales ===
  hubspot: { name: 'HubSpot', slug: 'hubspot', color: '#FF7A59', bgColor: '#FFF0EC' },
  salesforce: { name: 'Salesforce', slug: 'salesforce', color: '#00A1E0', bgColor: '#E6F6FC' },
  pipedrive: { name: 'Pipedrive', slug: 'pipedrive', color: '#26292C', bgColor: '#F0F0F0' },
  zohoCrm: { name: 'Zoho CRM', slug: 'zoho', color: '#C8202B', bgColor: '#FAE8E9' },
  freshworksCrm: { name: 'Freshworks', slug: 'freshworks', color: '#F26732', bgColor: '#FDE9E2' },
  freshsales: { name: 'Freshsales', slug: 'freshworks', color: '#F26732', bgColor: '#FDE9E2' },
  freshdesk: { name: 'Freshdesk', slug: 'freshdesk', color: '#01A66F', bgColor: '#E6F6F0' },
  freshservice: { name: 'Freshservice', slug: 'freshworks', color: '#F26732', bgColor: '#FDE9E2' },
  activeCampaign: { name: 'ActiveCampaign', slug: 'activecampaign', color: '#356AE6', bgColor: '#E9EFFE' },
  agile: { name: 'Agile CRM', slug: '', color: '#6EC1E4', bgColor: '#EFF8FC' },
  copper: { name: 'Copper', slug: 'copper', color: '#14C1B1', bgColor: '#E7F8F6' },
  highLevel: { name: 'HighLevel', slug: '', color: '#FF6B35', bgColor: '#FFEDE6' },
  keap: { name: 'Keap', slug: 'keap', color: '#4D9A3F', bgColor: '#ECF5EB' },
  drift: { name: 'Drift', slug: 'drift', color: '#0077FF', bgColor: '#E6F1FF' },
  close: { name: 'Close', slug: '', color: '#3E5BF5', bgColor: '#EBEFFE' },

  // === Databases ===
  airtable: { name: 'Airtable', slug: 'airtable', color: '#18BFFF', bgColor: '#E6F9FF' },
  supabase: { name: 'Supabase', slug: 'supabase', color: '#3ECF8E', bgColor: '#E8FAF2' },
  postgres: { name: 'PostgreSQL', slug: 'postgresql', color: '#4169E1', bgColor: '#E8EFF6' },
  mysql: { name: 'MySQL', slug: 'mysql', color: '#4479A1', bgColor: '#E6F3F6' },
  mongoDb: { name: 'MongoDB', slug: 'mongodb', color: '#47A248', bgColor: '#E6FCF0' },
  redis: { name: 'Redis', slug: 'redis', color: '#DC382D', bgColor: '#FCE8E7' },
  elasticsearch: { name: 'Elasticsearch', slug: 'elasticsearch', color: '#005571', bgColor: '#E6EEF1' },
  mariaDb: { name: 'MariaDB', slug: 'mariadb', color: '#003545', bgColor: '#E6EBED' },
  sqlite: { name: 'SQLite', slug: 'sqlite', color: '#003B57', bgColor: '#E6EBEE' },
  couchDb: { name: 'CouchDB', slug: 'apachecouchdb', color: '#E42528', bgColor: '#FCE8E9' },
  snowflake: { name: 'Snowflake', slug: 'snowflake', color: '#29B5E8', bgColor: '#E8F7FC' },
  questDb: { name: 'QuestDB', slug: 'questdb', color: '#D14671', bgColor: '#FAE9EE' },
  timescaleDb: { name: 'TimescaleDB', slug: 'timescale', color: '#FDB515', bgColor: '#FEF6E3' },
  cockroachDb: { name: 'CockroachDB', slug: '', color: '#6933FF', bgColor: '#EFEBFF' },
  qdrant: { name: 'Qdrant', slug: 'qdrant', color: '#DC244C', bgColor: '#FCE8EB' },
  pinecone: { name: 'Pinecone', slug: 'pinecone', color: '#000000', bgColor: '#F0F0F0' },
  weaviate: { name: 'Weaviate', slug: 'weaviate', color: '#2C2C2C', bgColor: '#EBEBEB' },
  milvus: { name: 'Milvus', slug: 'milvus', color: '#00A1EA', bgColor: '#E6F5FC' },
  chroma: { name: 'Chroma', slug: '', color: '#F0B90B', bgColor: '#FEF6E2' },
  zep: { name: 'Zep', slug: '', color: '#6D28D9', bgColor: '#F0EAFE' },
  nocodb: { name: 'NocoDB', slug: 'nocodb', color: '#3366FF', bgColor: '#E9EEFF' },
  neon: { name: 'Neon', slug: 'neon', color: '#00E0CC', bgColor: '#E6FAF8' },
  planetscale: { name: 'PlanetScale', slug: 'planetscale', color: '#000000', bgColor: '#F0F0F0' },
  upstash: { name: 'Upstash', slug: 'upstash', color: '#00E9A3', bgColor: '#E6FBF4' },
  fauna: { name: 'Fauna', slug: 'fauna', color: '#3A1AB6', bgColor: '#EBE7F8' },
  xata: { name: 'Xata', slug: 'xata', color: '#9161FA', bgColor: '#F3EFFE' },
  turso: { name: 'Turso', slug: 'turso', color: '#4FF8D2', bgColor: '#ECFDF9' },
  oracleDb: { name: 'Oracle', slug: 'oracle', color: '#F80000', bgColor: '#FFE6E6' },

  // === AI & LLM ===
  openAi: { name: 'OpenAI', slug: 'openai', color: '#412991', bgColor: '#EDE9F6' },
  anthropic: { name: 'Anthropic', slug: 'anthropic', color: '#191919', bgColor: '#F0F0F0' },
  huggingFace: { name: 'Hugging Face', slug: 'huggingface', color: '#FFD21E', bgColor: '#FFF9E1' },
  cohere: { name: 'Cohere', slug: 'cohere', color: '#D18EE2', bgColor: '#F9F0FB' },
  mistral: { name: 'Mistral', slug: 'mistral', color: '#FF7000', bgColor: '#FFF0E6' },
  ollama: { name: 'Ollama', slug: 'ollama', color: '#000000', bgColor: '#F0F0F0' },
  groq: { name: 'Groq', slug: 'groq', color: '#F55036', bgColor: '#FEECE9' },
  deepSeek: { name: 'DeepSeek', slug: '', color: '#0066FF', bgColor: '#E6F0FF' },
  xAi: { name: 'xAI', slug: 'x', color: '#000000', bgColor: '#F0F0F0' },
  awsBedrock: { name: 'AWS Bedrock', slug: 'amazonaws', color: '#FF9900', bgColor: '#FFF4E6' },
  azureOpenAi: { name: 'Azure OpenAI', slug: 'microsoftazure', color: '#0078D4', bgColor: '#E3F2FD' },

  // === Cloud Providers ===
  aws: { name: 'AWS', slug: 'amazonaws', color: '#FF9900', bgColor: '#FFF4E6' },
  amazonWebServices: { name: 'AWS', slug: 'amazonaws', color: '#FF9900', bgColor: '#FFF4E6' },
  awsS3: { name: 'S3', slug: 'amazons3', color: '#569A31', bgColor: '#EDF5E9' },
  s3: { name: 'S3', slug: 'amazons3', color: '#569A31', bgColor: '#EDF5E9' },
  awsLambda: { name: 'Lambda', slug: 'awslambda', color: '#FF9900', bgColor: '#FFF4E6' },
  awsSns: { name: 'SNS', slug: 'amazonaws', color: '#FF9900', bgColor: '#FFF4E6' },
  awsSqs: { name: 'SQS', slug: 'amazonsqs', color: '#FF4F8B', bgColor: '#FFECF2' },
  awsDynamoDb: { name: 'DynamoDB', slug: 'amazondynamodb', color: '#4053D6', bgColor: '#EAECFB' },
  awsElb: { name: 'ELB', slug: 'awselasticloadbalancing', color: '#8C4FFF', bgColor: '#F2ECFF' },
  awsTextract: { name: 'Textract', slug: 'amazonaws', color: '#FF9900', bgColor: '#FFF4E6' },
  awsTranscribe: { name: 'Transcribe', slug: 'amazonaws', color: '#FF9900', bgColor: '#FFF4E6' },
  awsRekognition: { name: 'Rekognition', slug: 'amazonaws', color: '#FF9900', bgColor: '#FFF4E6' },
  awsComprehend: { name: 'Comprehend', slug: 'amazonaws', color: '#FF9900', bgColor: '#FFF4E6' },
  awsSes: { name: 'SES', slug: 'amazonsimpleemailservice', color: '#DD344C', bgColor: '#FCE8EB' },
  googleCloud: { name: 'Google Cloud', slug: 'googlecloud', color: '#4285F4', bgColor: '#E8F0FE' },
  azure: { name: 'Azure', slug: 'microsoftazure', color: '#0078D4', bgColor: '#E3F2FD' },
  cloudflare: { name: 'Cloudflare', slug: 'cloudflare', color: '#F38020', bgColor: '#FEF0E4' },
  digitalOcean: { name: 'DigitalOcean', slug: 'digitalocean', color: '#0080FF', bgColor: '#E6F3FF' },
  linode: { name: 'Linode', slug: 'linode', color: '#00A95C', bgColor: '#E6F6EE' },
  vultr: { name: 'Vultr', slug: 'vultr', color: '#007BFC', bgColor: '#E6F2FF' },
  hetzner: { name: 'Hetzner', slug: 'hetzner', color: '#D50C2D', bgColor: '#FAE7EA' },

  // === DevOps & CI/CD ===
  github: { name: 'GitHub', slug: 'github', color: '#181717', bgColor: '#F0F0F0' },
  gitlab: { name: 'GitLab', slug: 'gitlab', color: '#FC6D26', bgColor: '#FEF0E8' },
  bitbucket: { name: 'Bitbucket', slug: 'bitbucket', color: '#0052CC', bgColor: '#E3ECFB' },
  jenkins: { name: 'Jenkins', slug: 'jenkins', color: '#D24939', bgColor: '#FAE9E7' },
  circleCI: { name: 'CircleCI', slug: 'circleci', color: '#343434', bgColor: '#EBEBEB' },
  travisCi: { name: 'Travis CI', slug: 'travisci', color: '#3EAAAF', bgColor: '#E8F5F6' },
  gitea: { name: 'Gitea', slug: 'gitea', color: '#609926', bgColor: '#EFF5E8' },
  gogs: { name: 'Gogs', slug: 'gogs', color: '#66D7D2', bgColor: '#EFF9F9' },
  docker: { name: 'Docker', slug: 'docker', color: '#2496ED', bgColor: '#E8F4FD' },
  kubernetes: { name: 'Kubernetes', slug: 'kubernetes', color: '#326CE5', bgColor: '#E9EFFE' },
  terraform: { name: 'Terraform', slug: 'terraform', color: '#7B42BC', bgColor: '#F1EAFA' },
  ansible: { name: 'Ansible', slug: 'ansible', color: '#EE0000', bgColor: '#FDE6E6' },
  grafana: { name: 'Grafana', slug: 'grafana', color: '#F46800', bgColor: '#FEEFE2' },
  prometheus: { name: 'Prometheus', slug: 'prometheus', color: '#E6522C', bgColor: '#FCECE7' },
  datadog: { name: 'Datadog', slug: 'datadog', color: '#632CA6', bgColor: '#F0E9F8' },
  newRelic: { name: 'New Relic', slug: 'newrelic', color: '#008C99', bgColor: '#E6F4F5' },
  sentry: { name: 'Sentry', slug: 'sentry', color: '#362D59', bgColor: '#EBEAF0' },
  pagerDuty: { name: 'PagerDuty', slug: 'pagerduty', color: '#06AC38', bgColor: '#E6F6EB' },
  netlify: { name: 'Netlify', slug: 'netlify', color: '#00C7B7', bgColor: '#E6F9F7' },
  vercel: { name: 'Vercel', slug: 'vercel', color: '#000000', bgColor: '#F0F0F0' },
  render: { name: 'Render', slug: 'render', color: '#46E3B7', bgColor: '#EBFBF5' },
  railway: { name: 'Railway', slug: 'railway', color: '#0B0D0E', bgColor: '#E6E6E7' },
  fly: { name: 'Fly.io', slug: 'flydotio', color: '#8B5CF6', bgColor: '#F3EFFE' },
  buildkite: { name: 'Buildkite', slug: 'buildkite', color: '#14CC80', bgColor: '#E7F9F0' },
  rundeck: { name: 'Rundeck', slug: 'rundeck', color: '#F73F39', bgColor: '#FDE9E8' },

  // === E-commerce ===
  stripe: { name: 'Stripe', slug: 'stripe', color: '#635BFF', bgColor: '#F0EFFE' },
  shopify: { name: 'Shopify', slug: 'shopify', color: '#7AB55C', bgColor: '#F3F9E8' },
  wooCommerce: { name: 'WooCommerce', slug: 'woocommerce', color: '#96588A', bgColor: '#F4ECF3' },
  payPal: { name: 'PayPal', slug: 'paypal', color: '#00457C', bgColor: '#E6EDF3' },
  squareNode: { name: 'Square', slug: 'square', color: '#3E4348', bgColor: '#ECEDEF' },
  brex: { name: 'Brex', slug: 'brex', color: '#1A1A1A', bgColor: '#E8E8E8' },
  gumroad: { name: 'Gumroad', slug: 'gumroad', color: '#36A9AE', bgColor: '#E8F5F6' },
  lemonsqueezy: { name: 'Lemon Squeezy', slug: 'lemonsqueezy', color: '#FFC233', bgColor: '#FFF8E6' },
  paddle: { name: 'Paddle', slug: 'paddle', color: '#FFDE2D', bgColor: '#FFFBE7' },
  bigCommerce: { name: 'BigCommerce', slug: 'bigcommerce', color: '#121118', bgColor: '#E8E7E8' },
  magento: { name: 'Magento', slug: 'magento', color: '#EE672F', bgColor: '#FDEFE9' },
  saleor: { name: 'Saleor', slug: 'saleor', color: '#1DBB93', bgColor: '#E8F8F3' },
  medusa: { name: 'Medusa', slug: 'medusa', color: '#7C3AED', bgColor: '#F2EBFE' },
  mollie: { name: 'Mollie', slug: 'mollie', color: '#000000', bgColor: '#F0F0F0' },
  chargebee: { name: 'Chargebee', slug: 'chargebee', color: '#FF6200', bgColor: '#FFF0E6' },

  // === Social Media ===
  twitter: { name: 'X', slug: 'x', color: '#000000', bgColor: '#F0F0F0' },
  linkedIn: { name: 'LinkedIn', slug: 'linkedin', color: '#0A66C2', bgColor: '#E6F0F8' },
  facebook: { name: 'Facebook', slug: 'facebook', color: '#1877F2', bgColor: '#E7F2FE' },
  facebookGraph: { name: 'Facebook', slug: 'facebook', color: '#1877F2', bgColor: '#E7F2FE' },
  instagram: { name: 'Instagram', slug: 'instagram', color: '#E4405F', bgColor: '#FCE9ED' },
  tiktok: { name: 'TikTok', slug: 'tiktok', color: '#000000', bgColor: '#F0F0F0' },
  pinterest: { name: 'Pinterest', slug: 'pinterest', color: '#BD081C', bgColor: '#F9E7E9' },
  reddit: { name: 'Reddit', slug: 'reddit', color: '#FF4500', bgColor: '#FFE9E0' },
  medium: { name: 'Medium', slug: 'medium', color: '#000000', bgColor: '#F0F0F0' },
  tumblr: { name: 'Tumblr', slug: 'tumblr', color: '#36465D', bgColor: '#EBECEF' },
  buffer: { name: 'Buffer', slug: 'buffer', color: '#231F20', bgColor: '#E8E8E8' },
  spotify: { name: 'Spotify', slug: 'spotify', color: '#1DB954', bgColor: '#E7F8EE' },
  strava: { name: 'Strava', slug: 'strava', color: '#FC4C02', bgColor: '#FEECE4' },
  vimeo: { name: 'Vimeo', slug: 'vimeo', color: '#1AB7EA', bgColor: '#E7F7FD' },

  // === Support & Helpdesk ===
  zendesk: { name: 'Zendesk', slug: 'zendesk', color: '#03363D', bgColor: '#E6F0F1' },
  intercom: { name: 'Intercom', slug: 'intercom', color: '#6AFDEF', bgColor: '#EEF9FC' },
  crisp: { name: 'Crisp', slug: 'crisp', color: '#1972F5', bgColor: '#E7F1FE' },
  helpScout: { name: 'Help Scout', slug: 'helpscout', color: '#1292EE', bgColor: '#E7F4FE' },
  serviceNow: { name: 'ServiceNow', slug: 'servicenow', color: '#81B5A1', bgColor: '#F0F6F4' },
  zammad: { name: 'Zammad', slug: 'zammad', color: '#FFBF00', bgColor: '#FFF7E0' },
  gorgias: { name: 'Gorgias', slug: 'gorgias', color: '#FF505A', bgColor: '#FFECEC' },
  tawk: { name: 'tawk.to', slug: 'tawk', color: '#03A84E', bgColor: '#E6F6ED' },
  livechat: { name: 'LiveChat', slug: 'livechat', color: '#FFD000', bgColor: '#FFF9E0' },
  kayako: { name: 'Kayako', slug: 'kayako', color: '#FF6A1A', bgColor: '#FFF0E8' },

  // === Forms & Surveys ===
  typeform: { name: 'Typeform', slug: 'typeform', color: '#262627', bgColor: '#E9E9E9' },
  jotForm: { name: 'JotForm', slug: 'jotform', color: '#FF6100', bgColor: '#FFF0E6' },
  formstack: { name: 'Formstack', slug: 'formstack', color: '#21B573', bgColor: '#E8F7F0' },
  surveyMonkey: { name: 'SurveyMonkey', slug: 'surveymonkey', color: '#00BF6F', bgColor: '#E6F8F0' },
  tally: { name: 'Tally', slug: 'tally', color: '#FF3E80', bgColor: '#FFEAF1' },
  wufoo: { name: 'Wufoo', slug: 'wufoo', color: '#880E27', bgColor: '#F5E7EA' },

  // === File Storage ===
  dropbox: { name: 'Dropbox', slug: 'dropbox', color: '#0061FF', bgColor: '#E6F0FF' },
  box: { name: 'Box', slug: 'box', color: '#0061D5', bgColor: '#E6F0FA' },
  egnyte: { name: 'Egnyte', slug: 'egnyte', color: '#00968F', bgColor: '#E6F4F3' },
  nextCloud: { name: 'Nextcloud', slug: 'nextcloud', color: '#0082C9', bgColor: '#E6F3F9' },
  ownCloud: { name: 'ownCloud', slug: 'owncloud', color: '#0082C9', bgColor: '#E6F3F9' },

  // === Scheduling & Calendar ===
  calendly: { name: 'Calendly', slug: 'calendly', color: '#006BFF', bgColor: '#E6F0FF' },
  cal: { name: 'Cal.com', slug: 'caldotcom', color: '#292929', bgColor: '#EAEAEA' },
  acuityScheduling: { name: 'Acuity', slug: 'acuityscheduling', color: '#3D3D3D', bgColor: '#ECECEC' },
  doodle: { name: 'Doodle', slug: 'doodle', color: '#0084FF', bgColor: '#E6F3FF' },

  // === Analytics & Tracking ===
  segment: { name: 'Segment', slug: 'segment', color: '#52BD95', bgColor: '#ECF8F3' },
  mixpanel: { name: 'Mixpanel', slug: 'mixpanel', color: '#7856FF', bgColor: '#F1ECFF' },
  amplitude: { name: 'Amplitude', slug: 'amplitude', color: '#015ACC', bgColor: '#E6EFFA' },
  plausible: { name: 'Plausible', slug: 'plausible', color: '#5850EC', bgColor: '#EFECFE' },
  posthog: { name: 'PostHog', slug: 'posthog', color: '#FFCC00', bgColor: '#FFF9E0' },
  matomo: { name: 'Matomo', slug: 'matomo', color: '#3152A0', bgColor: '#E9ECFA' },
  hotjar: { name: 'Hotjar', slug: 'hotjar', color: '#FD3A5C', bgColor: '#FFE9EC' },
  clarity: { name: 'Clarity', slug: 'clarity', color: '#0078D4', bgColor: '#E3F2FD' },

  // === Security & Authentication ===
  okta: { name: 'Okta', slug: 'okta', color: '#007DC1', bgColor: '#E6F3F9' },
  auth0: { name: 'Auth0', slug: 'auth0', color: '#EB5424', bgColor: '#FDECE7' },
  oneLogin: { name: 'OneLogin', slug: 'onelogin', color: '#0070C9', bgColor: '#E6F1F9' },
  duo: { name: 'Duo', slug: 'duo', color: '#6CC04A', bgColor: '#EEF8EB' },
  onePassword: { name: '1Password', slug: '1password', color: '#0094F5', bgColor: '#E6F4FE' },
  bitwarden: { name: 'Bitwarden', slug: 'bitwarden', color: '#175DDC', bgColor: '#E7EEFB' },
  hashicorpVault: { name: 'Vault', slug: 'vault', color: '#FFEC6E', bgColor: '#FFFBEC' },
  snyk: { name: 'Snyk', slug: 'snyk', color: '#4C4A73', bgColor: '#ECEDF2' },
  splunk: { name: 'Splunk', slug: 'splunk', color: '#000000', bgColor: '#F0F0F0' },
  crowdstrike: { name: 'CrowdStrike', slug: 'crowdstrike', color: '#FF0000', bgColor: '#FFE6E6' },
  securityScorecard: { name: 'SecurityScorecard', slug: 'securityscorecard', color: '#7033FD', bgColor: '#F1EBFF' },

  // === HR & Recruitment ===
  bambooHr: { name: 'BambooHR', slug: 'bamboo', color: '#73C41D', bgColor: '#F0F9E7' },
  workday: { name: 'Workday', slug: 'workday', color: '#0875E1', bgColor: '#E6F2FC' },
  greenhouse: { name: 'Greenhouse', slug: 'greenhouse', color: '#3AB549', bgColor: '#EAF6EB' },
  lever: { name: 'Lever', slug: 'lever', color: '#1E3B47', bgColor: '#E8EBED' },
  personio: { name: 'Personio', slug: 'personio', color: '#73B566', bgColor: '#F0F7EE' },
  deel: { name: 'Deel', slug: 'deel', color: '#15357A', bgColor: '#E7EAEF' },
  seatable: { name: 'SeaTable', slug: 'seatable', color: '#FF8000', bgColor: '#FFF2E6' },
  workable: { name: 'Workable', slug: 'workable', color: '#00756F', bgColor: '#E6F1F0' },

  // === Marketing & Advertising ===
  marketoNode: { name: 'Marketo', slug: 'marketo', color: '#5C4C9F', bgColor: '#EFECF5' },
  klaviyo: { name: 'Klaviyo', slug: 'klaviyo', color: '#0E0D0E', bgColor: '#E6E6E6' },
  iterable: { name: 'Iterable', slug: 'iterable', color: '#76CCD7', bgColor: '#F0F9FA' },
  lemlist: { name: 'Lemlist', slug: 'lemlist', color: '#6B4FFE', bgColor: '#EFECFF' },
  hunter: { name: 'Hunter', slug: 'hunter', color: '#FF6B2C', bgColor: '#FFF0E9' },
  clearbit: { name: 'Clearbit', slug: 'clearbit', color: '#03D6FA', bgColor: '#E6FAFD' },
  phantombuster: { name: 'PhantomBuster', slug: 'phantombuster', color: '#6C5CE7', bgColor: '#EFECFE' },

  // === CMS & Content ===
  wordpress: { name: 'WordPress', slug: 'wordpress', color: '#21759B', bgColor: '#E8F1F5' },
  contentful: { name: 'Contentful', slug: 'contentful', color: '#2478CC', bgColor: '#E8F2FA' },
  strapi: { name: 'Strapi', slug: 'strapi', color: '#4945FF', bgColor: '#ECEAFF' },
  sanity: { name: 'Sanity', slug: 'sanity', color: '#F03E2F', bgColor: '#FDE8E6' },
  ghost: { name: 'Ghost', slug: 'ghost', color: '#15171A', bgColor: '#E7E8E8' },
  webflow: { name: 'Webflow', slug: 'webflow', color: '#4353FF', bgColor: '#ECEEFF' },
  directus: { name: 'Directus', slug: 'directus', color: '#6644FF', bgColor: '#EFEBFF' },
  prismic: { name: 'Prismic', slug: 'prismic', color: '#5163BA', bgColor: '#EEEFFB' },
  storyblok: { name: 'Storyblok', slug: 'storyblok', color: '#09B3AF', bgColor: '#E6F7F7' },
  datocms: { name: 'DatoCMS', slug: 'datocms', color: '#FF7751', bgColor: '#FFF0EB' },
  hygraph: { name: 'Hygraph', slug: 'hygraph', color: '#090E24', bgColor: '#E6E7E9' },

  // === Automation & Integration ===
  n8n: { name: 'n8n', slug: 'n8n', color: '#EA4B71', bgColor: '#FDE9EE' },
  zapier: { name: 'Zapier', slug: 'zapier', color: '#FF4A00', bgColor: '#FFEBE0' },
  make: { name: 'Make', slug: 'make', color: '#6D00CC', bgColor: '#F0E5FF' },
  ifttt: { name: 'IFTTT', slug: 'ifttt', color: '#000000', bgColor: '#F0F0F0' },
  workato: { name: 'Workato', slug: 'workato', color: '#F0572B', bgColor: '#FDECE7' },

  // === Utilities & Core ===
  httpRequest: { name: 'HTTP', slug: '', color: '#6366F1', bgColor: '#EEF2FF' },
  webhook: { name: 'Webhook', slug: '', color: '#059669', bgColor: '#ECFDF5' },
  cron: { name: 'Cron', slug: '', color: '#4CAF50', bgColor: '#E8F5E9' },
  schedule: { name: 'Schedule', slug: '', color: '#4CAF50', bgColor: '#E8F5E9' },
  scheduleTrigger: { name: 'Schedule', slug: '', color: '#4CAF50', bgColor: '#E8F5E9' },
  ftp: { name: 'FTP', slug: '', color: '#607D8B', bgColor: '#ECEFF1' },
  ssh: { name: 'SSH', slug: '', color: '#607D8B', bgColor: '#ECEFF1' },
  rssFeedRead: { name: 'RSS', slug: 'rss', color: '#FFA500', bgColor: '#FFF5E6' },
  xml: { name: 'XML', slug: '', color: '#FF6600', bgColor: '#FFF0E6' },
  graphql: { name: 'GraphQL', slug: 'graphql', color: '#E10098', bgColor: '#FDE6F3' },
  mqtt: { name: 'MQTT', slug: 'mqtt', color: '#660066', bgColor: '#F2E6F2' },
  rabbitmq: { name: 'RabbitMQ', slug: 'rabbitmq', color: '#FF6600', bgColor: '#FFF0E6' },
  kafka: { name: 'Kafka', slug: 'apachekafka', color: '#231F20', bgColor: '#E8E8E8' },
  amqp: { name: 'AMQP', slug: '', color: '#FF6600', bgColor: '#FFF0E6' },

  // === Core n8n nodes ===
  code: { name: 'Code', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  function: { name: 'Function', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  functionItem: { name: 'Function', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  set: { name: 'Set', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  if: { name: 'IF', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  switch: { name: 'Switch', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  merge: { name: 'Merge', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  splitInBatches: { name: 'Split', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  wait: { name: 'Wait', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  noOp: { name: 'No Op', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  start: { name: 'Start', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  stickyNote: { name: 'Note', slug: '', color: '#FFD93D', bgColor: '#FFF9E6' },
  executeWorkflow: { name: 'Workflow', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  errorTrigger: { name: 'Error', slug: '', color: '#FF4444', bgColor: '#FFE6E6' },
  respondToWebhook: { name: 'Response', slug: '', color: '#059669', bgColor: '#ECFDF5' },
  crypto: { name: 'Crypto', slug: '', color: '#7B1FA2', bgColor: '#F3E5F5' },
  dateTime: { name: 'Date', slug: '', color: '#607D8B', bgColor: '#ECEFF1' },
  editImage: { name: 'Image', slug: '', color: '#E91E63', bgColor: '#FCE4EC' },
  compression: { name: 'Compression', slug: '', color: '#607D8B', bgColor: '#ECEFF1' },
  html: { name: 'HTML', slug: 'html5', color: '#E34F26', bgColor: '#FCE9E5' },
  htmlExtract: { name: 'HTML', slug: 'html5', color: '#E34F26', bgColor: '#FCE9E5' },
  readBinaryFile: { name: 'Read File', slug: '', color: '#607D8B', bgColor: '#ECEFF1' },
  writeBinaryFile: { name: 'Write File', slug: '', color: '#607D8B', bgColor: '#ECEFF1' },
  readBinaryFiles: { name: 'Read Files', slug: '', color: '#607D8B', bgColor: '#ECEFF1' },
  moveBinaryData: { name: 'Move Data', slug: '', color: '#607D8B', bgColor: '#ECEFF1' },
  extractFromFile: { name: 'Extract', slug: '', color: '#607D8B', bgColor: '#ECEFF1' },
  convertToFile: { name: 'Convert', slug: '', color: '#607D8B', bgColor: '#ECEFF1' },
  itemLists: { name: 'Lists', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  spreadsheetFile: { name: 'Spreadsheet', slug: 'microsoftexcel', color: '#217346', bgColor: '#E8F1EC' },
  filter: { name: 'Filter', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  limit: { name: 'Limit', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  removeDuplicates: { name: 'Dedupe', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  sort: { name: 'Sort', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  summarize: { name: 'Summarize', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  aggregate: { name: 'Aggregate', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  splitOut: { name: 'Split Out', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },
  renameKeys: { name: 'Rename', slug: '', color: '#FF6D5A', bgColor: '#FFEEEB' },

  // === Productivity ===
  evernote: { name: 'Evernote', slug: 'evernote', color: '#00A82D', bgColor: '#E6F6EA' },
  coda: { name: 'Coda', slug: 'coda', color: '#F46A54', bgColor: '#FDEEED' },
  miro: { name: 'Miro', slug: 'miro', color: '#FFD02F', bgColor: '#FFF9E7' },
  figma: { name: 'Figma', slug: 'figma', color: '#F24E1E', bgColor: '#FDE9E5' },
  loom: { name: 'Loom', slug: 'loom', color: '#625DF5', bgColor: '#EEECFE' },

  // === Data & ETL ===
  dbt: { name: 'dbt', slug: 'dbt', color: '#FF694B', bgColor: '#FFEFEB' },
  airbyte: { name: 'Airbyte', slug: 'airbyte', color: '#615EFF', bgColor: '#EEEDFF' },
  fivetran: { name: 'Fivetran', slug: 'fivetran', color: '#0073FF', bgColor: '#E6F1FF' },

  // === IoT ===
  homeAssistant: { name: 'Home Assistant', slug: 'homeassistant', color: '#41BDF5', bgColor: '#E9F7FE' },
  philipsHue: { name: 'Philips Hue', slug: 'philipshue', color: '#0065D3', bgColor: '#E6F0FA' },

  // === Messaging & SMS ===
  twilio: { name: 'Twilio', slug: 'twilio', color: '#F22F46', bgColor: '#FFE8EB' },
  vonage: { name: 'Vonage', slug: 'vonage', color: '#FFFFFF', bgColor: '#1A1A1A' },
  messagebird: { name: 'MessageBird', slug: 'messagebird', color: '#2481D7', bgColor: '#E8F2FC' },
  plivo: { name: 'Plivo', slug: 'plivo', color: '#57D377', bgColor: '#EDF9F0' },
  bandwidth: { name: 'Bandwidth', slug: 'bandwidth', color: '#079CEE', bgColor: '#E6F5FD' },

  // === Video & Media ===
  mux: { name: 'Mux', slug: 'mux', color: '#FB3271', bgColor: '#FFE9EF' },
  cloudinary: { name: 'Cloudinary', slug: 'cloudinary', color: '#3448C5', bgColor: '#E9EBFA' },
  uploadcare: { name: 'Uploadcare', slug: 'uploadcare', color: '#00B4FF', bgColor: '#E6F7FF' },
  dailyMotion: { name: 'Dailymotion', slug: 'dailymotion', color: '#0D0D0D', bgColor: '#E6E6E6' },
  wistia: { name: 'Wistia', slug: 'wistia', color: '#54BBFF', bgColor: '#EDF7FF' },

  // === Crypto & Blockchain ===
  ethereum: { name: 'Ethereum', slug: 'ethereum', color: '#3C3C3D', bgColor: '#ECECEC' },
  bitcoin: { name: 'Bitcoin', slug: 'bitcoin', color: '#F7931A', bgColor: '#FEF4E7' },
  binance: { name: 'Binance', slug: 'binance', color: '#F0B90B', bgColor: '#FEF6E2' },
  coinGecko: { name: 'CoinGecko', slug: 'coingecko', color: '#8DC63F', bgColor: '#F3F9EA' },
  kraken: { name: 'Kraken', slug: 'kraken', color: '#5741D9', bgColor: '#EFEBFB' },
  coinbase: { name: 'Coinbase', slug: 'coinbase', color: '#0052FF', bgColor: '#E6EEFF' },

  // === Accounting ===
  xero: { name: 'Xero', slug: 'xero', color: '#13B5EA', bgColor: '#E7F7FD' },
  quickbooks: { name: 'QuickBooks', slug: 'quickbooks', color: '#2CA01C', bgColor: '#E8F5E6' },
  harvest: { name: 'Harvest', slug: 'harvest', color: '#FA5D00', bgColor: '#FFEDE0' },
  invoiceNinja: { name: 'Invoice Ninja', slug: 'invoiceninja', color: '#000000', bgColor: '#F0F0F0' },

  // === Knowledge Base ===
  confluence: { name: 'Confluence', slug: 'confluence', color: '#172B4D', bgColor: '#E7E9EC' },
  gitbook: { name: 'GitBook', slug: 'gitbook', color: '#3884FF', bgColor: '#E9F1FF' },
  readme: { name: 'ReadMe', slug: 'readme', color: '#018EF5', bgColor: '#E6F4FE' },
  slite: { name: 'Slite', slug: 'slite', color: '#4D4AFF', bgColor: '#ECEBFF' },

  // === Monitoring ===
  uptimeRobot: { name: 'UptimeRobot', slug: 'uptimerobot', color: '#3BD671', bgColor: '#EAF9EF' },
  statuspage: { name: 'Statuspage', slug: 'statuspage', color: '#0052CC', bgColor: '#E3ECFB' },

  // === Password Managers ===
  onePasswordConnect: { name: '1Password', slug: '1password', color: '#0094F5', bgColor: '#E6F4FE' },
  lastpass: { name: 'LastPass', slug: 'lastpass', color: '#D32D27', bgColor: '#FAE9E8' },

  // === Misc Popular ===
  appwrite: { name: 'Appwrite', slug: 'appwrite', color: '#FD366E', bgColor: '#FFE9EF' },
  deepl: { name: 'DeepL', slug: 'deepl', color: '#0F2B46', bgColor: '#E6E9EC' },
  eventbrite: { name: 'Eventbrite', slug: 'eventbrite', color: '#F05537', bgColor: '#FDECE8' },
  metabase: { name: 'Metabase', slug: 'metabase', color: '#509EE3', bgColor: '#ECF5FC' },
  odoo: { name: 'Odoo', slug: 'odoo', color: '#714B67', bgColor: '#F1ECF0' },
  openWeatherMap: { name: 'OpenWeatherMap', slug: 'openweathermap', color: '#EB6E4B', bgColor: '#FDEEEA' },
  tableau: { name: 'Tableau', slug: 'tableau', color: '#E97627', bgColor: '#FDF1E8' },
  wise: { name: 'Wise', slug: 'wise', color: '#9FE870', bgColor: '#F4FDE8' },
  zoom: { name: 'Zoom', slug: 'zoom', color: '#2D8CFF', bgColor: '#E9F4FF' },
  ciscoWebex: { name: 'Webex', slug: 'webex', color: '#1EB980', bgColor: '#E7F8F2' },
  clockify: { name: 'Clockify', slug: 'clockify', color: '#03A9F4', bgColor: '#E6F6FE' },
  cloudConvert: { name: 'CloudConvert', slug: 'cloudconvert', color: '#E14D43', bgColor: '#FCEAE8' },
  discourse: { name: 'Discourse', slug: 'discourse', color: '#000000', bgColor: '#F0F0F0' },
  disqus: { name: 'Disqus', slug: 'disqus', color: '#2E9FFF', bgColor: '#E9F5FF' },
  elastic: { name: 'Elastic', slug: 'elastic', color: '#005571', bgColor: '#E6EEF1' },
  getResponse: { name: 'GetResponse', slug: 'getresponse', color: '#00BAFF', bgColor: '#E6F8FF' },
  goToWebinar: { name: 'GoToWebinar', slug: 'gotomeeting', color: '#F68D2E', bgColor: '#FEF1E7' },
  grist: { name: 'Grist', slug: 'grist', color: '#16B378', bgColor: '#E7F7F1' },
  mapbox: { name: 'Mapbox', slug: 'mapbox', color: '#4264FB', bgColor: '#ECEFFE' },
  mautic: { name: 'Mautic', slug: 'mautic', color: '#4E5E9E', bgColor: '#ECEEFC' },
  mediaWiki: { name: 'MediaWiki', slug: 'mediawiki', color: '#006699', bgColor: '#E6F0F5' },
  meetup: { name: 'Meetup', slug: 'meetup', color: '#ED1C40', bgColor: '#FDE7EA' },
  pushbullet: { name: 'Pushbullet', slug: 'pushbullet', color: '#4AB367', bgColor: '#EBF7EE' },
  pushover: { name: 'Pushover', slug: 'pushover', color: '#2C9BB8', bgColor: '#E9F5F8' },
  raindrop: { name: 'Raindrop', slug: 'raindrop', color: '#0076E7', bgColor: '#E6F1FD' },
  totp: { name: 'TOTP', slug: '', color: '#7B1FA2', bgColor: '#F3E5F5' },
  theHive: { name: 'TheHive', slug: '', color: '#FF8600', bgColor: '#FFF2E6' },
  toggl: { name: 'Toggl', slug: 'toggltrack', color: '#E57CD8', bgColor: '#FCF0FB' },
  quickbase: { name: 'Quickbase', slug: 'quickbase', color: '#2B7DDB', bgColor: '#E8F1FC' },

  // LangChain specific
  agent: { name: 'AI Agent', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  chainLlm: { name: 'LLM Chain', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  chainRetrievalQa: { name: 'Q&A Chain', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  chainSummarization: { name: 'Summary', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  chat: { name: 'Chat', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  chatTrigger: { name: 'Chat', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  informationExtractor: { name: 'Extractor', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  guardrails: { name: 'Guardrails', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  documentDefaultDataLoader: { name: 'Data Loader', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  documentGithubLoader: { name: 'GitHub Loader', slug: 'github', color: '#181717', bgColor: '#F0F0F0' },
  documentJsonInputLoader: { name: 'JSON Loader', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  documentBinaryInputLoader: { name: 'Binary Loader', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  textSplitterCharacter: { name: 'Text Splitter', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  textSplitterRecursiveCharacter: { name: 'Text Splitter', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  textSplitterTokenSplitter: { name: 'Token Splitter', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  vectorStoreInMemory: { name: 'Vector Store', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  memoryBufferWindow: { name: 'Memory', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  memoryPostgresChat: { name: 'Postgres Memory', slug: 'postgresql', color: '#4169E1', bgColor: '#E8EFF6' },
  memoryRedisChat: { name: 'Redis Memory', slug: 'redis', color: '#DC382D', bgColor: '#FCE8E7' },
  memoryXata: { name: 'Xata Memory', slug: 'xata', color: '#9161FA', bgColor: '#F3EFFE' },
  memoryZep: { name: 'Zep Memory', slug: '', color: '#6D28D9', bgColor: '#F0EAFE' },
  outputParserAutofixing: { name: 'Output Parser', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  outputParserItemList: { name: 'List Parser', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  outputParserStructured: { name: 'Structured Parser', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  toolCalculator: { name: 'Calculator', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  toolCode: { name: 'Code Tool', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  toolHttpRequest: { name: 'HTTP Tool', slug: '', color: '#6366F1', bgColor: '#EEF2FF' },
  toolWikipedia: { name: 'Wikipedia', slug: 'wikipedia', color: '#000000', bgColor: '#F0F0F0' },
  toolWorkflow: { name: 'Workflow Tool', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  toolSerpApi: { name: 'SerpAPI', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
  toolWolframAlpha: { name: 'Wolfram', slug: 'wolframalpha', color: '#DD1100', bgColor: '#FCE7E4' },
  retrievalWorkflow: { name: 'Retrieval', slug: '', color: '#10A37F', bgColor: '#E7F6F2' },
};

// Extract base service name from node type
// n8n-nodes-base.slackTrigger -> slack
// @n8n/n8n-nodes-langchain.toolSlack -> slack
function getBaseServiceName(nodeType: string): string {
  // Get the part after the last dot
  const nodeName = nodeType.split('.').pop() || '';

  // Remove common suffixes and prefixes
  let baseName = nodeName
    .replace(/Trigger$/i, '')
    .replace(/Tool$/i, '')
    .replace(/HitlTool$/i, '')
    .replace(/Node$/i, '')
    .replace(/^tool/i, '')
    .replace(/^lmChat/i, '')
    .replace(/^lm/i, '')
    .replace(/^embeddings/i, '');

  // Convert first char to lowercase for matching
  if (baseName.length > 0) {
    baseName = baseName.charAt(0).toLowerCase() + baseName.slice(1);
  }

  return baseName;
}

// Get integration icon info for a node type
export function getIntegrationIcon(nodeType: string): IntegrationIconInfo | null {
  const baseName = getBaseServiceName(nodeType);

  // Try exact match first
  if (BASE_ICONS[baseName]) {
    return BASE_ICONS[baseName];
  }

  // Try with first letter lowercase
  const lowerFirst = baseName.charAt(0).toLowerCase() + baseName.slice(1);
  if (BASE_ICONS[lowerFirst]) {
    return BASE_ICONS[lowerFirst];
  }

  return null;
}
